import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  MenuItem,
  Pagination,
  Paper,
  Skeleton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { MdAdd, MdCancel, MdCheckCircle, MdDeleteForever, MdEdit, MdEmail, MdHourglassTop, MdMarkEmailUnread, MdPhone, MdSearch, MdStorefront, MdTune, MdVisibility } from 'react-icons/md';
import { postData, getData, putData, deleteData, postFileUpload, patchData } from '../../Webservice/Webservice';
import SellerForm from '../../components/Seller/SellerForm';
import SellerDetails from './SellerDetails';
import { indianStates } from '../../Utils/states';
import { FONT, SellerAvatar, StatusChip, authToken } from './sellerShared';

const PAGE_SIZE = 20;
const iconBtn = (color, bg) => ({ color, background: bg, borderRadius: '8px', '&:hover': { background: bg, filter: 'brightness(.95)' } });

const initialSellerForm = {
  name: '', email: '', password: '', mobile: '',
  contactPerson2Name: '', contactPerson2Email: '', contactPerson2Mobile: '', contactPerson2Password: '',
  business_name: '', businessAddress: '', sellerLicenseNumber: '', businessCity: '', businessState: '', businessPincode: '',
  gstNumber: '', businessPAN: '',
  addressProof: null, gstProof: null, businessPanProof: null, shopActLicense: null, msmeUdyamCertificate: null, cancelledCheque: null,
  storeDescription: '', storeAddress: '', storeArea: '', storeNearbyLandmark: '', storeCity: '', storeState: '', storePincode: '',
  storeLatitude: '', storeLongitude: '',
  commissionPercentage: '10',
  bankAccountHolder: '', bankName: '', bankAccountNumber: '', bankIfsc: '', bankUpiId: '',
  status: 'pending',
};
const EMPTY_FILTERS = { mobile: '', email: '', business_name: '', businessState: '', gstNumber: '', businessPAN: '', storeCity: '', storeState: '', minCommission: '', maxCommission: '' };

const Sellermanagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sellerId } = useParams();
  const editMode = !!sellerId && location.state?.mode === 'edit';

  // list
  const [sellers, setSellers] = useState([]);
  const [stats, setStats] = useState({ total: 0, approved: 0, waiting: 0, rejected: 0, inactive: 0 });
  const [meta, setMeta] = useState({ page: 1, totalPages: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [temp, setTemp] = useState(EMPTY_FILTERS);
  const [drawer, setDrawer] = useState(false);

  // form
  const [showAddForm, setShowAddForm] = useState(false);
  const [sellerForm, setSellerForm] = useState(initialSellerForm);
  const [isEditing, setIsEditing] = useState(false);
  const [currentSellerId, setCurrentSellerId] = useState(null);

  // dialogs
  const [mailOpen, setMailOpen] = useState(false);
  const [mailEmail, setMailEmail] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [statusTarget, setStatusTarget] = useState(null); // { seller, next }
  const [busy, setBusy] = useState(false);

  const fetchSellers = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const params = { page: meta.page, limit: PAGE_SIZE };
      if (search) params.search = search;
      if (status) params.status = status;
      if (activeFilter) params.isActive = activeFilter;
      Object.entries(filters).forEach(([k, v]) => { if (v !== '') params[k] = v; });
      const res = await getData('seller/list', params, authToken(), false);
      if (res && Array.isArray(res.seller)) {
        setSellers(res.seller);
        if (res.stats) setStats(res.stats);
        const m = res.meta || {};
        setMeta({ page: m.page || 1, totalPages: m.totalPages || 1, total: m.total || 0 });
        setError('');
      } else {
        setSellers([]);
        setMeta({ page: 1, totalPages: 1, total: 0 });
      }
    } catch (e) {
      setSellers([]);
      setError('Could not load sellers. Please try again.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta.page, search, status, activeFilter, filters]);

  useEffect(() => { if (!sellerId && !showAddForm) fetchSellers(); }, [fetchSellers, sellerId, showAddForm]);

  // wait for the user to stop typing before searching
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput.trim()); setMeta((p) => ({ ...p, page: 1 })); }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  // edit page: load the seller into the form
  useEffect(() => {
    if (!editMode) {
      if (!sellerId) { setShowAddForm(false); setIsEditing(false); setCurrentSellerId(null); }
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await getData(`seller/${sellerId}`, {}, authToken(), false);
        if (cancelled) return;
        if (!r?._id) return navigate('/sellermanagement');
        const bank = r.bankDetails || {};
        setSellerForm({
          ...initialSellerForm,
          ...Object.fromEntries(Object.keys(initialSellerForm).map((k) => [k, r[k] ?? initialSellerForm[k]])),
          password: '',
          contactPerson2Password: '',
          commissionPercentage: r.commissionPercentage != null ? String(r.commissionPercentage) : '10',
          bankAccountHolder: bank.accountHolder || '',
          bankName: bank.bankName || '',
          bankAccountNumber: bank.accountNumber || '',
          bankIfsc: bank.ifsc || '',
          bankUpiId: bank.upiId || '',
          status: r.status || 'pending',
        });
        setCurrentSellerId(sellerId);
        setIsEditing(true);
        setShowAddForm(true);
      } catch (e) {
        navigate('/sellermanagement');
      }
    })();
    return () => { cancelled = true; };
  }, [editMode, sellerId, navigate]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setSellerForm((p) => ({ ...p, [name]: value }));
  };
  const handleFileUpload = (e) => {
    const { name } = e.target;
    setSellerForm((p) => ({ ...p, [name]: e.target.files[0] }));
  };

  const handleAddSeller = async () => {
    const fd = new FormData();
    Object.keys(sellerForm).forEach((key) => {
      const v = sellerForm[key];
      if (v === null || v === undefined) return;
      if (typeof v === 'string' && v.startsWith('http') && /Proof|Certificate|Cheque|License|eSignature/i.test(key)) return; // existing file, unchanged
      if ((key === 'password' || key === 'contactPerson2Password') && !v) return;
      fd.append(key, v);
    });
    try {
      if (isEditing) await putData(`seller/update/${currentSellerId}`, fd, authToken());
      else await postFileUpload('seller/add', fd, authToken());
    } catch (err) {
      return; // the service already showed the reason; keep the form open
    }
    setSellerForm(initialSellerForm);
    setShowAddForm(false);
    setIsEditing(false);
    setCurrentSellerId(null);
    setMeta((p) => ({ ...p, page: 1 }));
    navigate('/sellermanagement');
  };

  const closeForm = (val) => {
    if (!val) {
      setShowAddForm(false);
      setIsEditing(false);
      setSellerForm(initialSellerForm);
      navigate(sellerId ? `/sellermanagement/${sellerId}` : '/sellermanagement');
    }
  };

  // ---------- actions ----------
  const confirmStatus = async () => {
    if (!statusTarget) return;
    setBusy(true);
    try {
      await putData(`seller/statusUpdate/${statusTarget.seller._id}`, { status: statusTarget.next }, authToken());
      await fetchSellers(true);
    } catch (e) {
      setError('Could not change the status.');
    } finally {
      setBusy(false);
      setStatusTarget(null);
    }
  };

  const toggleActive = async (s) => {
    try {
      await patchData(`seller/toggle-active/${s._id}`, {}, authToken());
      setSellers((list) => list.map((x) => (x._id === s._id ? { ...x, isActive: !x.isActive } : x)));
      fetchSellers(true);
    } catch (e) {
      setError('Could not change the active state.');
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setBusy(true);
    try {
      await deleteData(`seller/delete/${deleteTarget._id}`, authToken());
      setDeleteTarget(null);
      if (sellers.length === 1 && meta.page > 1) setMeta((p) => ({ ...p, page: p.page - 1 }));
      else await fetchSellers(true);
    } catch (e) {
      setError('Could not delete the seller.');
      setDeleteTarget(null);
    } finally {
      setBusy(false);
    }
  };

  const sendInvite = async () => {
    setBusy(true);
    try {
      await postData('seller/register-link', { email: mailEmail.trim() }, authToken());
      setMailOpen(false);
      setMailEmail('');
    } catch (e) {
      /* the service shows why */
    } finally {
      setBusy(false);
    }
  };

  const reset = (setter) => (v) => { setter(v); setMeta((p) => ({ ...p, page: 1 })); };
  const hasFilters = !!(search || status || activeFilter || Object.values(filters).some(Boolean));
  const clearAll = () => {
    setSearchInput(''); setSearch(''); setStatus(''); setActiveFilter('');
    setFilters(EMPTY_FILTERS); setTemp(EMPTY_FILTERS); setMeta((p) => ({ ...p, page: 1 }));
  };

  // ---------- view page ----------
  if (sellerId && !editMode) {
    return <SellerDetails sellerId={sellerId} onEdit={() => navigate(`/sellermanagement/${sellerId}`, { state: { mode: 'edit' } })} />;
  }

  // ---------- add / edit page ----------
  if (showAddForm || editMode) {
    return (
      <SellerForm
        sellerForm={sellerForm}
        handleFormChange={handleFormChange}
        handleFileUpload={handleFileUpload}
        handleAddSeller={handleAddSeller}
        isEditing={isEditing}
        setShowAddForm={closeForm}
      />
    );
  }

  const kpis = [
    { label: 'All sellers', value: stats.total, icon: MdStorefront, gradient: 'linear-gradient(135deg,#6366f1,#8b5cf6)', on: () => setStatus(''), active: !status },
    { label: 'Approved', value: stats.approved, icon: MdCheckCircle, gradient: 'linear-gradient(135deg,#10b981,#059669)', on: () => setStatus('approved'), active: status === 'approved' },
    { label: 'Need review', value: stats.waiting, icon: MdHourglassTop, gradient: 'linear-gradient(135deg,#f59e0b,#ea580c)', on: () => setStatus('waitingForApproval'), active: status === 'waitingForApproval' },
    { label: 'Rejected', value: stats.rejected, icon: MdCancel, gradient: 'linear-gradient(135deg,#ef4444,#dc2626)', on: () => setStatus('rejected'), active: status === 'rejected' },
  ];

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, background: '#f8fafc', minHeight: '100vh' }}>
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
          <Box>
            <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 24, color: '#0f172a' }}>Sellers</Typography>
            <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>Onboard sellers, review their documents and decide who can sell.</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <Button startIcon={<MdMarkEmailUnread />} onClick={() => setMailOpen(true)} sx={{ fontFamily: FONT, textTransform: 'none', fontWeight: 700, borderRadius: '10px', px: 2.5, color: '#0f172a', background: '#fff', border: '1px solid #cbd5e1', '&:hover': { background: '#f1f5f9' } }}>Invite by email</Button>
            <Button variant="contained" startIcon={<MdAdd />} onClick={() => { setSellerForm(initialSellerForm); setIsEditing(false); setShowAddForm(true); }} sx={{ fontFamily: FONT, background: 'rgb(255 209 2)', color: 'black', textTransform: 'none', borderRadius: '10px', fontWeight: 700, px: 3, boxShadow: '0 4px 12px rgba(255, 209, 2, 0.3)', border: '1px solid black', '&:hover': { background: 'rgb(255 209 2)', transform: 'translateY(-1px)' } }}>Add Seller</Button>
          </Box>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 2, mb: 3 }}>
          {kpis.map((k) => (
            <Paper key={k.label} elevation={0} onClick={() => { k.on(); setMeta((p) => ({ ...p, page: 1 })); }} sx={{ p: 2.25, borderRadius: '16px', color: '#fff', background: k.gradient, display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer', outline: k.active && k.label !== 'All sellers' ? '3px solid #0f172a' : 'none', outlineOffset: 2, boxShadow: '0 4px 12px rgba(15,23,42,.1)', transition: 'transform .15s', '&:hover': { transform: 'translateY(-2px)' } }}>
              <Box sx={{ background: 'rgba(255,255,255,.22)', borderRadius: '14px', p: 1.4, display: 'flex' }}><k.icon size={24} /></Box>
              <Box>
                <Typography sx={{ fontSize: 12, fontWeight: 700, opacity: 0.92, fontFamily: FONT }}>{k.label}</Typography>
                <Typography sx={{ fontSize: 25, fontWeight: 800, lineHeight: 1.2, fontFamily: FONT }}>{k.value}</Typography>
              </Box>
            </Paper>
          ))}
        </Box>

        <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: '14px', border: '1px solid #e2e8f0', display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField size="small" placeholder="Search name, business, email, mobile, GST or seller code" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} sx={{ flex: 1, minWidth: 260 }} InputProps={{ startAdornment: <MdSearch size={20} style={{ marginRight: 8, color: '#94a3b8' }} /> }} />
          <TextField select size="small" label="Approval" value={status} onChange={(e) => reset(setStatus)(e.target.value)} sx={{ minWidth: 180 }}>
            <MenuItem value="">All</MenuItem><MenuItem value="pending">Pending</MenuItem><MenuItem value="waitingForApproval">Waiting for approval</MenuItem><MenuItem value="approved">Approved</MenuItem><MenuItem value="rejected">Rejected</MenuItem>
          </TextField>
          <TextField select size="small" label="Active" value={activeFilter} onChange={(e) => reset(setActiveFilter)(e.target.value)} sx={{ minWidth: 120 }}>
            <MenuItem value="">All</MenuItem><MenuItem value="true">Active</MenuItem><MenuItem value="false">Inactive</MenuItem>
          </TextField>
          <Button startIcon={<MdTune />} onClick={() => { setTemp(filters); setDrawer(true); }} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569', border: '1px solid #e2e8f0', borderRadius: '10px', px: 2 }}>More filters</Button>
          {hasFilters && <Button onClick={clearAll} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Clear</Button>}
        </Paper>

        <TableContainer component={Paper} elevation={0} sx={{ borderRadius: '14px', border: '1px solid #e2e8f0', boxShadow: '0 4px 20px rgba(15,23,42,.05)' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ '& th': { backgroundColor: '#f8fafc', fontWeight: 800, py: 1.6, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#475569', fontFamily: FONT, borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap' } }}>
                <TableCell>Seller</TableCell><TableCell>Contact</TableCell><TableCell>Location</TableCell><TableCell align="right">Products</TableCell><TableCell align="right">Commission</TableCell><TableCell>Approval</TableCell><TableCell align="center">Active</TableCell><TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: 6 }).map((_, i) => <TableRow key={i}>{Array.from({ length: 8 }).map((__, j) => <TableCell key={j}><Skeleton height={34} /></TableCell>)}</TableRow>)
              ) : sellers.length === 0 ? (
                <TableRow><TableCell colSpan={8} sx={{ py: 6, textAlign: 'center' }}><Typography sx={{ fontFamily: FONT, color: '#64748b', fontWeight: 600 }}>{hasFilters ? 'No sellers match your filters.' : 'No sellers yet. Add one or send an invite.'}</Typography></TableCell></TableRow>
              ) : sellers.map((s) => (
                <TableRow key={s._id} hover onClick={() => navigate(`/sellermanagement/${s._id}`)} sx={{ cursor: 'pointer', '& td': { borderBottom: '1px solid #f1f5f9', py: 1.4, fontFamily: FONT } }}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <SellerAvatar seller={s} size={38} />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 13.5, color: '#0f172a' }}>{s.business_name || s.name}</Typography>
                        <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#94a3b8' }}>{s.name}{s.sellerCode ? ` · ${s.sellerCode}` : ''}</Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}><MdEmail size={13} color="#94a3b8" /><Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: '#334155' }}>{s.email || '—'}</Typography></Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}><MdPhone size={13} color="#94a3b8" /><Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#64748b' }}>{s.mobile || '—'}</Typography></Box>
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: 13 }}>{s.businessCity || '—'}</Typography>
                    <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#94a3b8' }}>{s.businessState || ''}</Typography>
                  </TableCell>
                  <TableCell align="right"><Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 13.5 }}>{s.productCount || 0}</Typography></TableCell>
                  <TableCell align="right"><Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: 13 }}>{s.commissionPercentage != null ? `${s.commissionPercentage}%` : '—'}</Typography></TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <StatusChip status={s.status} />
                      {s.status !== 'approved' && <Tooltip title="Approve" arrow><IconButton size="small" onClick={() => setStatusTarget({ seller: s, next: 'approved' })} sx={iconBtn('#059669', '#ecfdf5')}><MdCheckCircle size={16} /></IconButton></Tooltip>}
                      {s.status !== 'rejected' && <Tooltip title="Reject" arrow><IconButton size="small" onClick={() => setStatusTarget({ seller: s, next: 'rejected' })} sx={iconBtn('#dc2626', 'rgba(220,38,38,.1)')}><MdCancel size={16} /></IconButton></Tooltip>}
                    </Box>
                  </TableCell>
                  <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                    <Tooltip title={s.isActive ? 'Deactivate' : 'Activate'} arrow><span><Switch size="small" checked={!!s.isActive} onChange={() => toggleActive(s)} color="success" /></span></Tooltip>
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                    <Tooltip title="View" arrow><IconButton size="small" onClick={() => navigate(`/sellermanagement/${s._id}`)} sx={{ ...iconBtn('#4f46e5', 'rgba(79,70,229,.1)'), mr: 0.75 }}><MdVisibility size={16} /></IconButton></Tooltip>
                    <Tooltip title="Edit" arrow><IconButton size="small" onClick={() => navigate(`/sellermanagement/${s._id}`, { state: { mode: 'edit' } })} sx={{ ...iconBtn('#ca8a04', '#fefce8'), mr: 0.75 }}><MdEdit size={16} /></IconButton></Tooltip>
                    <Tooltip title="Delete" arrow><IconButton size="small" onClick={() => setDeleteTarget(s)} sx={iconBtn('#dc2626', 'rgba(220,38,38,.1)')}><MdDeleteForever size={18} /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {meta.total > 0 && (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination count={meta.totalPages} page={meta.page} shape="rounded" showFirstButton showLastButton onChange={(_, p) => setMeta((m) => ({ ...m, page: p }))} sx={{ '& .MuiPagination-ul': { flexWrap: 'nowrap' }, '& .MuiPaginationItem-root': { fontFamily: FONT, fontWeight: 600, borderRadius: '8px', '&.Mui-selected': { backgroundColor: 'rgb(255, 209, 2)', color: 'black', '&:hover': { backgroundColor: 'rgb(255, 190, 0)' } } } }} />
            </Box>
            <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>Showing {(meta.page - 1) * PAGE_SIZE + 1} to {Math.min(meta.page * PAGE_SIZE, meta.total)} of {meta.total} sellers</Typography>
          </>
        )}
      </Box>

      <Drawer anchor="right" open={drawer} onClose={() => setDrawer(false)}>
        <Box sx={{ width: 340, p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 18 }}>More filters</Typography>
          <TextField fullWidth size="small" label="Business name" value={temp.business_name} onChange={(e) => setTemp((p) => ({ ...p, business_name: e.target.value }))} />
          <TextField fullWidth size="small" label="Mobile" value={temp.mobile} onChange={(e) => setTemp((p) => ({ ...p, mobile: e.target.value }))} />
          <TextField fullWidth size="small" label="Email" value={temp.email} onChange={(e) => setTemp((p) => ({ ...p, email: e.target.value }))} />
          <TextField select fullWidth size="small" label="Business state" value={temp.businessState} onChange={(e) => setTemp((p) => ({ ...p, businessState: e.target.value }))}>
            <MenuItem value="">Any</MenuItem>{indianStates.map((st) => <MenuItem key={st} value={st}>{st}</MenuItem>)}
          </TextField>
          <TextField fullWidth size="small" label="GST number" value={temp.gstNumber} onChange={(e) => setTemp((p) => ({ ...p, gstNumber: e.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase() }))} />
          <TextField fullWidth size="small" label="Business PAN" value={temp.businessPAN} onChange={(e) => setTemp((p) => ({ ...p, businessPAN: e.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase() }))} />
          <TextField fullWidth size="small" label="Store city" value={temp.storeCity} onChange={(e) => setTemp((p) => ({ ...p, storeCity: e.target.value }))} />
          <TextField select fullWidth size="small" label="Store state" value={temp.storeState} onChange={(e) => setTemp((p) => ({ ...p, storeState: e.target.value }))}>
            <MenuItem value="">Any</MenuItem>{indianStates.map((st) => <MenuItem key={st} value={st}>{st}</MenuItem>)}
          </TextField>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField fullWidth size="small" type="number" inputProps={{ min: 0 }} label="Min commission %" value={temp.minCommission} onChange={(e) => setTemp((p) => ({ ...p, minCommission: e.target.value }))} />
            <TextField fullWidth size="small" type="number" inputProps={{ min: 0 }} label="Max commission %" value={temp.maxCommission} onChange={(e) => setTemp((p) => ({ ...p, maxCommission: e.target.value }))} />
          </Box>
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <Button fullWidth onClick={() => { setTemp(EMPTY_FILTERS); setFilters(EMPTY_FILTERS); setDrawer(false); setMeta((p) => ({ ...p, page: 1 })); }} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569', border: '1px solid #e2e8f0' }}>Reset</Button>
            <Button fullWidth variant="contained" onClick={() => { setFilters(temp); setDrawer(false); setMeta((p) => ({ ...p, page: 1 })); }} sx={{ textTransform: 'none', fontWeight: 800, background: 'rgb(255 209 2)', color: 'black', '&:hover': { background: 'rgb(255 190 0)' } }}>Apply</Button>
          </Box>
        </Box>
      </Drawer>

      {/* invite */}
      <Dialog open={mailOpen} onClose={() => !busy && setMailOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogTitle sx={{ fontFamily: FONT, fontWeight: 800 }}>Invite a seller</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: FONT, fontSize: 13.5, color: '#64748b', mb: 2 }}>They get an email with a link to register their business details and documents.</Typography>
          <TextField autoFocus fullWidth size="small" type="email" label="Seller's email" value={mailEmail} onChange={(e) => setMailEmail(e.target.value)} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setMailOpen(false)} disabled={busy} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Cancel</Button>
          <Button onClick={sendInvite} disabled={busy || !/^\S+@\S+\.\S+$/.test(mailEmail.trim())} variant="contained" sx={{ textTransform: 'none', fontWeight: 800, background: 'rgb(255 209 2)', color: 'black', borderRadius: '10px', '&:hover': { background: 'rgb(255 190 0)' } }}>{busy ? 'Sending…' : 'Send invite'}</Button>
        </DialogActions>
      </Dialog>

      {/* approve / reject */}
      <Dialog open={!!statusTarget} onClose={() => !busy && setStatusTarget(null)} PaperProps={{ sx: { borderRadius: '14px', p: 1, minWidth: 380 } }}>
        <DialogTitle sx={{ fontFamily: FONT, fontWeight: 800 }}>{statusTarget?.next === 'approved' ? 'Approve seller?' : 'Reject seller?'}</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: FONT }}>
            {statusTarget?.next === 'approved'
              ? <><b>{statusTarget?.seller.business_name || statusTarget?.seller.name}</b> will be able to list and sell products.</>
              : <><b>{statusTarget?.seller.business_name || statusTarget?.seller.name}</b> will not be able to sell.</>} They are emailed about the decision.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setStatusTarget(null)} disabled={busy} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Cancel</Button>
          <Button onClick={confirmStatus} disabled={busy} variant="contained" sx={{ textTransform: 'none', fontWeight: 800, background: statusTarget?.next === 'approved' ? '#10b981' : '#dc2626', '&:hover': { background: statusTarget?.next === 'approved' ? '#059669' : '#b91c1c' } }}>{busy ? 'Working…' : statusTarget?.next === 'approved' ? 'Yes, approve' : 'Yes, reject'}</Button>
        </DialogActions>
      </Dialog>

      {/* delete */}
      <Dialog open={!!deleteTarget} onClose={() => !busy && setDeleteTarget(null)} PaperProps={{ sx: { borderRadius: '14px', p: 1, minWidth: 380 } }}>
        <DialogTitle sx={{ fontFamily: FONT, fontWeight: 800 }}>Delete seller?</DialogTitle>
        <DialogContent><Typography sx={{ fontFamily: FONT }}><b>{deleteTarget?.business_name || deleteTarget?.name}</b> will be removed and archived, together with their menu access, assigned products and product requests. This can't be undone from the panel.</Typography></DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setDeleteTarget(null)} disabled={busy} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Cancel</Button>
          <Button onClick={confirmDelete} disabled={busy} variant="contained" sx={{ textTransform: 'none', fontWeight: 700, background: '#dc2626', '&:hover': { background: '#b91c1c' } }}>{busy ? 'Deleting…' : 'Yes, delete'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Sellermanagement;
